/**
 * ClauseList — the whole clause list of the version being reviewed
 * (§13.7's first Verify assertion), plus the document-scoped findings that
 * ride on the FIRST clause's review row (session 4 FACTS 4: `scope:
 * 'document'` flags are a fact about the whole agreement — subject injury
 * absent, dispute resolution absent, screen-fail payment absent — and are
 * rendered in their own banner here, never as a flag of clause 1, which
 * would be actively misleading).
 *
 * The screen-fail-payment line (R26: "the review must say whether screen
 * fails are properly paid") is DERIVED from the document-scoped flags rather
 * than from the POST /review response's own `screen_fail_payment` object,
 * because that object is returned only by the review-run call and this list
 * is what renders on every load (including one where the review already ran
 * in an earlier session) — per the README, a `required_element` document
 * flag of topic `screen_fail_payment` fires exactly when it is NOT
 * addressed, so its absence is a valid (derived, G8) "addressed" reading.
 * Labelled `derived` in its own `title=` rather than stated as a raw fact.
 *
 * That derivation is only valid once a review has actually run: a version
 * with `reviewed_count: 0` has NO flags of any kind, so "no flag was raised"
 * is not evidence of anything and must never be affirmatively rendered as
 * "addressed" (G3 — the gap stays visible, absent is absent; a fix applied
 * after review found this line manufacturing an "addressed" verdict from a
 * clause list that had never been reviewed). The line is gated on
 * `data.reviewed_count > 0` and reads "not reviewed yet" until then.
 */
import { useMemo } from 'react';
import { FileText } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import type { ClauseDecision, ClauseFlag, ListClausesResult } from '../../api/clauses';
import { topicLabel } from './topics';
import { ClauseRow } from './ClauseRow';

interface ClauseListProps {
  data: ListClausesResult;
  decidingId: string | null;
  onDecide: (reviewId: string, decision: ClauseDecision) => void;
}

/** Document-scoped flags ride on the first clause's review row (§5.9 has no document-level row) — gather across every clause and dedupe (topic+kind+message) in case more than one clause happens to carry one. */
function collectDocumentFlags(clauses: ListClausesResult['clauses']): ClauseFlag[] {
  const seen = new Set<string>();
  const result: ClauseFlag[] = [];
  for (const clause of clauses) {
    for (const flag of clause.review?.flags ?? []) {
      if (flag.scope !== 'document') continue;
      const key = `${flag.topic}|${flag.kind}|${flag.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(flag);
    }
  }
  return result;
}

function DocumentFlagsBanner({ flags }: { flags: ClauseFlag[] }) {
  if (flags.length === 0) return null;
  return (
    <div
      data-testid="ctr-document-flags"
      className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs dark:border-red-800 dark:bg-red-900/20"
    >
      <p className="font-semibold text-red-800 dark:text-red-300">Document-level findings</p>
      <ul className="mt-1.5 space-y-1">
        {flags.map((f, idx) => (
          <li key={idx} className="text-red-700 dark:text-red-300">
            <span className="font-medium">{topicLabel(f.topic)}:</span> {f.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScreenFailPaymentLine({ addressed, message }: { addressed: boolean; message: string | null }) {
  return (
    <div
      data-testid="ctr-screen-fail-payment"
      title={
        addressed
          ? 'derived: no required-element flag for this topic was raised (R26)'
          : (message ?? 'not addressed (R26)')
      }
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
        addressed
          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
          : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
      }`}
    >
      Screen-fail payment: {addressed ? 'addressed' : 'not addressed'}
    </div>
  );
}

export function ClauseList({ data, decidingId, onDecide }: ClauseListProps) {
  const documentFlags = useMemo(() => collectDocumentFlags(data.clauses), [data.clauses]);
  const screenFail = useMemo(() => {
    const flag = documentFlags.find((f) => f.topic === 'screen_fail_payment' && f.kind === 'required_element');
    return { addressed: !flag, message: flag?.message ?? null };
  }, [documentFlags]);
  // A version that has never been reviewed has no flags at all, so "no flag
  // was raised" is not evidence of anything — it must not be affirmatively
  // rendered as "addressed" (G3: absence of data is not a computed verdict;
  // R26 requires the REVIEW to say so). Gate the derived line on there being
  // at least one review row to derive it from.
  const reviewed = data.reviewed_count > 0;

  if (data.clauses.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No clauses yet"
        hint="This version has not been parsed into clauses (plan §6, WP-29)."
        variant="card"
      />
    );
  }

  return (
    <div className="space-y-3">
      <DocumentFlagsBanner flags={documentFlags} />
      {reviewed ? (
        <ScreenFailPaymentLine addressed={screenFail.addressed} message={screenFail.message} />
      ) : (
        <div
          data-testid="ctr-screen-fail-payment"
          className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
          title="no clause review has run on this version yet (R26)"
        >
          Screen-fail payment: not reviewed yet
        </div>
      )}
      <div className="space-y-3">
        {data.clauses.map((clause) => (
          <ClauseRow
            key={clause.id}
            clause={clause}
            deciding={decidingId === clause.review?.id}
            onDecide={onDecide}
          />
        ))}
      </div>
    </div>
  );
}

export default ClauseList;
